import { MailCheck } from 'lucide-react';
import { useId, useState } from 'react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { PageHead } from '@/admin-area/ui/PageHead';
import { StateMessage } from '@/admin-area/ui/StateMessage';
import { Button } from '@/components/ui/button';
import { Skeleton, SkeletonRegion } from '@/components/ui/skeleton';
import { readErrorKey } from '@/lib/read-error';
import type { PendingMessage } from '../api-types';
import { recipientName } from '../format';
import { ApprovalCard } from './ApprovalCard';
import { ConfirmDialog } from '../ConfirmDialog';
import { EditSheet } from './EditSheet';
import { type ApprovalsState, useApprovals } from './use-approvals';

const DONE = { approve: 'approved', postpone: 'postponed', discard: 'discarded' } as const;

type Open = { kind: 'edit' | 'discard'; item: PendingMessage } | null;

type QueueProps = {
  readonly state: ApprovalsState;
  readonly busyId: string | null;
  readonly onReload: () => void;
  readonly onMore: () => void;
  readonly onApprove: (item: PendingMessage) => void;
  readonly onPostpone: (item: PendingMessage) => void;
  readonly onEdit: (item: PendingMessage) => void;
  readonly onDiscard: (item: PendingMessage) => void;
};

/** What sits under the head: loading, failed, empty, or one card per email. */
function Queue(props: QueueProps) {
  const { t } = useTranslation(['communications', 'errors']);
  const { state, busyId } = props;
  return (
    <>
      {state.status === 'loading' ? (
        <SkeletonRegion label={t('common.loading')} className="flex flex-col gap-2">
          {[0, 1, 2].map((n) => (
            <Skeleton key={n} className="h-40 w-full" />
          ))}
        </SkeletonRegion>
      ) : null}
      {state.status === 'error' ? (
        <StateMessage
          role="alert"
          tone="error"
          icon={MailCheck}
          title={t(readErrorKey(state.httpStatus))}
          action={
            <Button variant="outline" onClick={props.onReload}>
              {t('common.retry')}
            </Button>
          }
        />
      ) : null}
      {state.status === 'ready' && state.items.length === 0 ? (
        <StateMessage
          icon={MailCheck}
          title={t('approvals.empty')}
          text={t('approvals.emptyText')}
        />
      ) : null}
      {state.status === 'ready' && state.items.length > 0 ? (
        <ul aria-label={t('approvals.title')} className="flex flex-col gap-3">
          {state.items.map((item) => (
            <ApprovalCard
              key={item.message.id}
              item={item}
              actions={{
                busy: busyId === item.message.id,
                onApprove: () => props.onApprove(item),
                onEdit: () => props.onEdit(item),
                onPostpone: () => props.onPostpone(item),
                onDiscard: () => props.onDiscard(item),
              }}
            />
          ))}
        </ul>
      ) : null}
      {state.status === 'ready' && state.moreFailed ? (
        <p role="alert" className="text-destructive text-sm">
          {t('common.failed')}
        </p>
      ) : null}
      {state.status === 'ready' && state.nextCursor !== null ? (
        <Button
          variant="outline"
          className="min-h-11 self-center"
          disabled={state.loadingMore}
          onClick={props.onMore}
        >
          {t('approvals.more')}
        </Button>
      ) : null}
    </>
  );
}

/** «Comunicazioni → Da approvare» (spec §5.2): each onboarding email waits here for the Director. */
export function ApprovalsPage() {
  const { t } = useTranslation(['communications', 'errors']);
  const titleId = useId();
  const { state, busyId, failed, decide, loadMore, reload } = useApprovals();
  const [open, setOpen] = useState<Open>(null);

  const close = () => setOpen(null);
  const settle = (item: PendingMessage, action: Parameters<typeof decide>[1]) =>
    void decide(item.message.id, action).then((done) => {
      if (!done) return;
      close();
      toast.success(t(`approvals.toast.${DONE[action.kind]}`, { name: recipientName(item) }));
    });

  return (
    <section aria-labelledby={titleId} className="flex flex-col gap-4">
      <PageHead titleId={titleId} title={t('approvals.title')} summary={t('approvals.summary')} />
      {failed ? (
        <p role="alert" className="text-destructive text-sm">
          {t('common.failed')}
        </p>
      ) : null}
      <Queue
        state={state}
        busyId={busyId}
        onReload={reload}
        onMore={loadMore}
        onApprove={(item) => settle(item, { kind: 'approve' })}
        onPostpone={(item) => settle(item, { kind: 'postpone' })}
        onEdit={(item) => setOpen({ kind: 'edit', item })}
        onDiscard={(item) => setOpen({ kind: 'discard', item })}
      />
      {open?.kind === 'edit' ? (
        <EditSheet
          item={open.item}
          busy={busyId === open.item.message.id}
          onApprove={(edited) => settle(open.item, { kind: 'approve', edited })}
          onClose={close}
        />
      ) : null}
      {open?.kind === 'discard' ? (
        <ConfirmDialog
          title={t('approvals.discardTitle')}
          ask={t('approvals.discardAsk', { name: recipientName(open.item) })}
          confirmLabel={t('approvals.discardConfirm')}
          destructive
          busy={busyId === open.item.message.id}
          onConfirm={() => settle(open.item, { kind: 'discard' })}
          onClose={close}
        />
      ) : null}
    </section>
  );
}
